<script setup>
import { ref, computed, watch } from 'vue';
import { useGlobalStore } from '@/stores/global';

const props = defineProps({
  salon: { type: Object, default: null },
  service: { type: Object, default: null },
  name: { type: String, default: '' },
  brandsText: {
    type: String,
    default: () => '',
  },
  chooseAutocenterText: {
    type: String,
    default: () => '',
  },
  leaveRequestText: {
    type: String,
    default: () => '',
  },
  isServiceActive: {
    type: Boolean,
    default: () => false,
  },
  autocenterText: {
    type: String,
    default: () => '',
  },
  serviceCenterText: {
    type: String,
    default: () => '',
  },
});
const globalStore = useGlobalStore();

const hasSalon = computed(
  () =>
    props.salon &&
    Object.keys(props.salon).length > 0 &&
    props.salon.name &&
    props.salon.name.length > 0,
);
const hasService = computed(
  () =>
    props.service &&
    Object.keys(props.service).length > 0 &&
    props.service.name &&
    props.service.name.length > 0,
);
const hasBoth = computed(() => hasSalon.value && hasService.value);

const activeTab = ref(
  props.isServiceActive && hasService.value ? 'service' : 'salon',
);
watch(
  () => [props.service, props.isServiceActive],
  ([newService, serviceFilterActive]) => {
    if (
      serviceFilterActive &&
      newService &&
      Object.keys(newService).length > 0
    ) {
      activeTab.value = 'service';
    } else if (!serviceFilterActive && hasSalon.value) {
      activeTab.value = 'salon';
    }
  },
  { immediate: true },
);
const activeData = computed(() =>
  activeTab.value === 'salon' ? props.salon : props.service,
);

function handleApplicationClick(type, id) {
  globalStore.setApplicationPopup({ visible: true, type, centerId: id });
}
</script>

<template>
  <div class="mappoint mappoint--autocenter">
    <p
      v-if="hasService"
      class="mb-0 mappoint__title h4"
      v-html="service.name"
    ></p>
    <p v-else class="mb-0 mappoint__title h4" v-html="name"></p>
    <div v-if="hasBoth || hasService" class="mappoint__header-logic mt-3">
      <Tabs
        v-if="hasBoth"
        v-model:value="activeTab"
        class="mappoint__tabs-wrapper"
      >
        <TabList class="mappoint__tablist">
          <Tab value="salon" class="mappoint__tab mb-0 body-2-bold">{{
            autocenterText
          }}</Tab>
          <Tab value="service" class="mappoint__tab mb-0 body-2-bold">{{
            serviceCenterText
          }}</Tab>
        </TabList>
      </Tabs>
      <template v-else>
        <p v-if="hasService" class="mb-0 mappoint__type body-2-bold">
          {{ serviceCenterText }}
        </p>
      </template>
    </div>
    <div v-if="activeData" class="mappoint__content">
      <div class="mappoint__top">
        <div
          v-if="activeData.address || activeData.phone || activeData.time"
          class="mappoint__info"
        >
          <div
            v-if="activeData.address"
            class="mappoint__address body-3-medium"
            v-html="activeData.address"
          ></div>
          <div class="mappoint__contacts">
            <p
              v-if="activeData.phone"
              class="mb-0 mappoint__phone body-3-regular"
              v-html="activeData.phone"
            ></p>
            <p
              v-if="activeData.time"
              class="mb-0 mappoint__schedule body-3-regular"
              v-html="activeData.time"
            ></p>
          </div>
        </div>

        <div
          v-if="activeData.brands?.length"
          class="mappoint__list body-3-regular"
        >
          <div class="mappoint__brands">
            <p
              v-if="brandsText"
              class="mb-0 mappoint__brands-title"
              v-html="brandsText"
            ></p>
            <ul class="m-0 p-0 mappoint__brands-items">
              <li
                v-for="(brand, idx) in activeData.brands"
                :key="idx"
                class="m-0 mappoint__brands-item"
              >
                <NuxtImg
                  v-if="brand?.src"
                  :src="brand.src"
                  :alt="brand.alt"
                  :height="brand.height"
                  :width="brand.width"
                  class="mappoint__brands-item-image"
                  loading="lazy"
                />
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div class="mappoint__bottom">
        <Button
          v-if="activeData.link && chooseAutocenterText"
          v-slot="slotProps"
          as-child
          rounded
          variant="outlined"
        >
          <CTagDetect
            :to="activeData.link"
            target="_blank"
            :class="[
              slotProps.class,
              `js-button-map-autocenter-${activeData.id}`,
            ]"
            class="mappoint__button button-black link-normal"
          >
            {{ chooseAutocenterText }}
          </CTagDetect>
        </Button>

        <Button
          v-if="activeData.id && leaveRequestText"
          class="mappoint__button"
          :class="[`js-button-map-autocenter-application-${activeData.id}`]"
          rounded
          variant="text"
          @click="
            handleApplicationClick('autocenter_application', activeData.id)
          "
        >
          {{ leaveRequestText }}
        </Button>
      </div>
    </div>
  </div>
</template>
<style scoped lang="scss">
.mappoint {
  $parent: &;
  align-content: flex-start;
  background-color: $white;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 24px 24px 16px;
  height: 100%;
  @include media-breakpoint-up(sm) {
    gap: 40px;
    padding: 32px 32px 16px;
  }
  @include media-breakpoint-up(md) {
    padding: 32px;
  }
  @include media-breakpoint-up(xl) {
    @include responsive-spacing(border-radius, 8);
    @include responsive-spacing(gap, 40);
    @include responsive-spacing(padding, 32);
  }
  &__top {
    display: flex;
    flex-direction: column;
    gap: 16px;
    @include media-breakpoint-up(sm) {
      gap: 24px;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(gap, 24);
    }
  }
  &__content {
    display: flex;
    flex-direction: column;
    gap: 32px;
    height: 100%;
    @include media-breakpoint-up(md) {
      gap: 40px;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(gap, 40);
    }
  }
  &__phone {
    &:deep(a) {
      @include body-3-medium;
    }
  }
  &__tab {
    padding: 8px;
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(padding, 8);
    }
  }
  &__title {
    margin-bottom: 8px;
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(margin-bottom, 8);
    }
  }
  &__info {
    display: flex;
    flex-direction: column;
    gap: 16px;
    @include media-breakpoint-up(sm) {
      gap: 24px;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(gap, 24);
    }
  }
  &__list {
    border-top: 1px solid $divider;
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding-top: 16px;
    @include media-breakpoint-up(sm) {
      gap: 24px;
      padding-top: 24px;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(border-top-width, 1);
      @include responsive-spacing(gap, 24);
      @include responsive-spacing(padding-top, 24);
    }
  }
  &__schedule {
    color: $on-surface-variant-2;
  }
  &__brands {
    display: flex;
    gap: 8px;
    flex-direction: column;
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(gap, 8);
    }
    &-title {
      color: $on-surface-variant-2;
    }
    &-items {
      column-gap: 16px;
      display: flex;
      flex-direction: row;
      flex-wrap: wrap;
      list-style: none;
      row-gap: 4px;
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(column-gap, 16);
        @include responsive-spacing(row-gap, 4);
      }
    }
    &-item {
      width: 40px;
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(width, 40);
      }
      &-image {
        aspect-ratio: 1/1;
        height: auto;
        width: 100%;
      }
    }
  }
  &__bottom {
    display: grid;
    grid-template-columns: 1fr;
    gap: 16px;
    margin-top: auto;
    @include media-breakpoint-up(md) {
      grid-template-columns: auto auto;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(gap, 16);
    }
  }
  &--one-point {
    align-content: start;
    display: grid;
    grid-template-areas: 'title' 'info' 'bottom';
    padding-bottom: 24px;
    @include media-breakpoint-up(sm) {
      column-gap: 0;
      grid-template-columns: 6fr 1px 6fr;
      grid-template-areas: 'title line info' 'bottom . .';
      row-gap: 32px;
      padding-bottom: 32px;
    }
    @include media-breakpoint-up(md) {
      grid-template-columns: 1fr;
      grid-template-areas: 'title' 'info' 'bottom';
      grid-template-rows: auto auto 1fr;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(row-gap, 32);
      @include responsive-spacing(padding-bottom, 32);
    }
    #{$parent} {
      &__divider {
        display: none;
        grid-area: line;
        background-color: $divider;
        height: 100%;
        width: 1px;
        @include media-breakpoint-up(sm) {
          display: block;
        }
        @include media-breakpoint-up(md) {
          display: none;
        }
      }
      &__title {
        grid-area: title;
        @include h3;
        @include media-breakpoint-up(sm) {
          padding-right: 24px;
          @include h4();
        }
        @include media-breakpoint-up(md) {
          padding-right: 0;
        }
      }
      &__info {
        grid-area: info;
        @include media-breakpoint-up(sm) {
          padding-left: 24px;
        }
        @include media-breakpoint-up(md) {
          align-self: end;
          padding-left: 0;
          margin-top: auto;
        }
      }
      &__bottom {
        grid-area: bottom;
      }
      &__button {
        width: 100%;
        @include media-breakpoint-up(sm) {
          max-width: fit-content;
        }
      }
    }
  }
}
</style>
